<template>
  <UModal v-model="isOpen" prevent-close :ui="{ width: 'sm:max-w-3xl' }">
    <!-- Search Bar + Close Button -->
    <div class="flex items-center justify-between gap-4 mb-6">
      <UInput
        v-model="searchValue"
        placeholder="Search support needs"
        size="lg"
        icon="i-heroicons-magnifying-glass"
        :ui="{ icon: { trailing: { pointer: '' } } }"
        trailing
        class="w-3/4 md:w-3/5"
      >
        <template v-if="searchValue?.length" #trailing>
          <UButton
            variant="ghost"
            icon="i-heroicons-x-mark-20-solid"
            size="lg"
            :padded="false"
            class="hover:text-gray transition-colors duration-200"
            @click="searchValue = ''"
          />
        </template>
      </UInput>
      <UButton
        icon="heroicons:x-mark-20-solid"
        variant="ghost"
        size="xl"
        class="hover:text-gray transition-colors duration-200"
        @click="isOpen = false"
      />
    </div>

    <!-- A-Z Search Buttons -->
    <div class="flex flex-wrap mb-6 divide-x gap-y-2 divide-gray-200">
      <UButton
        v-for="letter in aToZ"
        :key="letter"
        :label="letter"
        variant="ghost"
        size="xl"
        :ui="{ rounded: 'rounded-none', padding: { xl: 'px-2 py-0 first:pl-0' } }"
        :class="{
          'font-brown font-bold': searchValue === letter,
          'font-light text-almostBlack': searchValue !== letter
        }"
        @click="searchByLetter(letter)"
      />
    </div>
    <div class="h-80 overflow-y-scroll custom-scrollbar pr-2">
      <div v-if="searchedSupportNeedCategories.length" class="flex flex-col items-start gap-6">
        <div
          v-for="supportCat in searchedSupportNeedCategories"
          :key="supportCat.uid"
          class="group relative inline-block"
        >
          <label
            class="text-xl font-bold mb-1 group-hover:text-green-600 transition-colors duration-200"
            :class="{ underline: filters.supportNeeds.includes(supportCat.uid) }"
            :for="'atz-filter-category' + supportCat.uid"
          >
            {{ supportCat.displayText }}
          </label>
          <p v-if="supportCat.glossaryDefinition" class="text-almostBlack text-sm font-light">
            {{ supportCat.glossaryDefinition }}
          </p>
          <input
            :id="'atz-filter-category-' + supportCat.uid"
            v-model="filters.supportNeeds"
            type="checkbox"
            class="absolute inset-0 cursor-pointer opacity-0"
            :value="supportCat.uid"
            :checked="filters.supportNeeds.includes(supportCat.uid)"
            @change="updateFilters"
          />
        </div>
      </div>
      <p v-else class="text-gray font-light text-sm">No support needs found</p>
    </div>
  </UModal>
</template>
<script setup lang="ts">
const frontendSupportNeedCategorySearchStore = useSupportNeedCategoryStore()

const store = useServiceSearchStore()
const { filters } = storeToRefs(store)

const supportNeedGlossaryStore = useSupportNeedGlossaryStore()
const { isOpen } = storeToRefs(supportNeedGlossaryStore)

const searchValue = defineModel('searchValue', { type: String, default: '' })
const aToZ = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
const searchByLetter = (letter: string) => {
  searchValue.value = letter
}

// Filter out support need categories based on search value
const searchedSupportNeedCategories = computed(() => {
  return frontendSupportNeedCategorySearchStore.filterCategoriesBySearchTerm(searchValue.value)
})

// Use this emit to tell the parent component the user has selected support needs they want
// and is ready to refresh the search results
const emit = defineEmits<{
  (event: 'support-needs-selected'): void
}>()

// Emit event and close the modal
const updateFilters = () => {
  emit('support-needs-selected')
  isOpen.value = false
}
</script>
